import { ArrowRight, ChevronLeft, Megaphone } from "lucide-react";
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs/create-base-dialog";
import { Button } from "@/ui-components/ui/button";
import { Input } from "@/ui-components/ui/input";
import { useState, useMemo } from "react";
import { useAdvertisementsCreate } from "@/lib/api/requests/advertisements.requests";
import { toast } from "@/lib/toast";
import { Separator } from "@/ui-components/ui/separator";
import { Label } from "@/ui-components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui-components/ui/select";
import { Switch } from "@/ui-components/ui/switch";

interface CreateAdvertisementDialogProps {
  open: boolean;
  onClose: (refetchData?: boolean) => void;
}

const steps = [
  {
    key: "name",
    title: "Schritt 1 von 6: Titel der Leistung",
    description:
      'Geben Sie der Leistung einen klaren Namen. Zum Beispiel: "Trikosponsor 2026/2027".',
  },
  {
    key: "social_media",
    title: "Schritt 2 von 6: Scoial Media",
    description:
      "Beinhaltet die Leistung die Platzierung des Sponsor Logos in Ihren Social Media Posts?",
  },
  {
    key: "basics",
    title: "Schritt 3 von 6: Grundwerte",
    description: "Legen Sie den geforderten Preis (Euro) fest.",
  },
  {
    key: "basics_2",
    title: "Schritt 4 von 6: Menge",
    description:
      "Falls die Leistung mehrere Objekte einzele verwaltet, zum Beispiel Banden, können sie hier die Anzahl hinterlegen",
  },
  {
    key: "timing",
    title: "Schritt 5 von 6: Zeitraum & Zahlungsweise",
    description:
      "Wählen Sie, ob es sich um eine Einmalzahlung handelt oder legen Sie den zeitlichen Rhythmus fest.",
  },
  {
    key: "summary",
    title: "Schritt 6 von 6: Zusammenfassung",
    description:
      "Überprüfen Sie alles in Ruhe. Wenn es passt, speichern Sie die Leistung.",
  },
];

const INTERVAL_UNITS = [
  { value: "DAY", label: "Tag" },
  { value: "WEEK", label: "Woche" },
  { value: "MONTH", label: "Monat" },
  { value: "YEAR", label: "Jahr" },
] as const;

const CreateAdvertisementDialog = ({
  open,
  onClose,
}: CreateAdvertisementDialogProps) => {
  const [name, setName] = useState("");
  const [maxConcurrentAllowed, setMaxConcurrentAllowed] = useState("");
  const [volumeEuro, setVolumeEuro] = useState("0");
  const [isOneTime, setIsOneTime] = useState(true);
  const [intervalUnit, setIntervalUnit] = useState<
    (typeof INTERVAL_UNITS)[number]["value"] | "DAY"
  >("DAY");
  const [durationCount, setDurationCount] = useState("1");
  const [showOnSocialMedia, setShowOnSocialMedia] = useState(false);
  const [isDonation, setIsDonation] = useState(false);

  const [stepIndex, setStepIndex] = useState(0);

  const { mutate: createAdvertisement, isPending } = useAdvertisementsCreate();

  const isNameValid = useMemo(() => name.trim().length > 0, [name]);
  const isBasicsValid = useMemo(() => {
    const vol = Number(volumeEuro);
    return Number.isFinite(vol) && vol >= 0;
  }, [volumeEuro]);
  const isTimingValid = useMemo(() => {
    if (isOneTime) return true;
    const dur = Number(durationCount);
    return String(intervalUnit).length > 0 && Number.isFinite(dur) && dur > 0;
  }, [isOneTime, intervalUnit, durationCount]);

  const isFormValid = isNameValid && isBasicsValid && isTimingValid;
  const canGoBack = stepIndex > 0;
  const canGoNext = useMemo(() => {
    if (stepIndex === 0) return isNameValid;
    if (stepIndex === 2) return isBasicsValid;
    if (stepIndex === 4) return isTimingValid;
    return true;
  }, [stepIndex, isNameValid, isBasicsValid, isTimingValid]);

  const goNext = () => {
    if (stepIndex === 0 && !isNameValid) return;
    if (stepIndex === 2 && !isBasicsValid) return;
    if (stepIndex === 4 && !isTimingValid) return;
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const handleSubmit = () => {
    if (!isFormValid) return;

    const payload: any = {
      name,
      max_concurrent_allowed:
        maxConcurrentAllowed.trim().length === 0
          ? undefined
          : Number(maxConcurrentAllowed),
      volume: Number(volumeEuro),
      show_on_social_media: showOnSocialMedia,
      type: isDonation ? "DONATION" : "SPONSORING",
    };

    if (!isOneTime) {
      payload.interval = intervalUnit;
      payload.duration = Number(durationCount);
    }

    createAdvertisement(payload, {
      onSuccess: () => {
        toast.success("Leistung erfolgreich erstellt");
        onClose(true);
      },
      onError: (error) => {
        toast.error("Leistung konnte nicht erstellt werden", { error });
      },
    });
  };

  const progressPercent = Math.round(((stepIndex + 1) / steps.length) * 100);

  const isLastStep = stepIndex === steps.length - 1;

  const footerActions = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={goBack}
        disabled={!canGoBack}
        className="h-11 w-full justify-center gap-2 rounded-xl border-gray-200 px-5 text-sm font-semibold text-gray-700 transition-all hover:border-gray-300 hover:bg-gray-50 sm:w-auto"
      >
        <ChevronLeft className="h-4 w-4" />
        Zurück
      </Button>
      {!isLastStep && (
        <Button
          type="button"
          onClick={goNext}
          disabled={!canGoNext}
          className="h-11 w-full justify-center gap-2 rounded-xl px-5 text-sm font-semibold shadow-sm sm:w-auto"
        >
          Weiter
          <ArrowRight className="h-4 w-4" />
        </Button>
      )}
    </>
  );

  return (
    <CreateBaseDialog
      open={open}
      onClose={onClose}
      icon={Megaphone}
      title="Neue Leistung erstellen"
      description="Legen Sie eine neue Sponsoring-Leistung an."
      isSaving={isPending}
      canSave={isFormValid}
      formId="create-advertisement-form"
      submitLabel="Speichern"
      hideSubmitButton={!isLastStep}
      customFooterActions={footerActions}
    >
      <form
        id="create-advertisement-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="flex flex-col h-full px-5 sm:px-6 py-4 overflow-y-auto"
      >
        {/* Fortschritt und Schritt-Header */}
        <div className="space-y-2 mb-4 shrink-0">
          <div className="text-sm text-muted-foreground">
            {steps[stepIndex].title}
          </div>
          <div className="text-xs text-muted-foreground">
            {steps[stepIndex].description}
          </div>
          <div className="w-full h-2 bg-muted rounded">
            <div
              className="h-2 bg-primary rounded"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <Separator className="mb-4 shrink-0" />

        <div className="flex-1 min-h-0 space-y-4">
          {/* Inhalt pro Schritt */}
          {stepIndex === 0 && (
            <div className="space-y-3">
              <div>
                <Label className="block mb-1">Titel der Leistung</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Zum Beispiel: Trikotsponsor Saison 2026/2027"
                />
                {!isNameValid && (
                  <p className="text-xs text-destructive mt-1">
                    Bitte geben Sie einen Titel ein.
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Tipp: Wählen Sie einen Namen, den Sie später leicht
                  wiederfinden.
                </p>
              </div>
            </div>
          )}

          {stepIndex === 1 && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50/50 mt-4">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">
                      Sponsor auf Social Media erwähnen
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Soll der Sponsor automatisch auf generierten Social Media
                      Posts erscheinen?
                    </p>
                  </div>
                  <Switch
                    checked={showOnSocialMedia}
                    onCheckedChange={setShowOnSocialMedia}
                  />
                </div>
              </div>
            </div>
          )}

          {stepIndex === 2 && (
            <div className="space-y-3">
              <div>
                <Label className="block mb-1">Geforderter Preis (Euro)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm">€</span>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={volumeEuro}
                    onChange={(e) => setVolumeEuro(e.target.value)}
                    placeholder="z.B. 1000,00"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Erklärung: Welchen Preis (in Euro) Sie für diese Leistung
                  verlangen.
                </p>
              </div>
              {!isBasicsValid && (
                <p className="text-xs text-destructive">
                  Bitte geben Sie sinnvolle Werte ein (Zahlen, nicht negativ).
                </p>
              )}
            </div>
          )}

          {stepIndex === 3 && (
            <div className="space-y-3">
              <div>
                <Label className="block mb-1">
                  Maximal gleichzeitig erlaubt
                </Label>
                <Input
                  type="number"
                  min={1}
                  value={maxConcurrentAllowed}
                  onChange={(e) => setMaxConcurrentAllowed(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Erklärung: Wie viele gleichzeitige Einblendungen derselben
                  Leistung erlaubt sind.
                </p>
              </div>
            </div>
          )}

          {stepIndex === 4 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50/50">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">
                    Spnsoring oder Spende
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Aktivieren, wenn dies eine Spende ist.
                  </p>
                </div>
                <Switch checked={isDonation} onCheckedChange={setIsDonation} />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50/50">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Einmalzahlung</Label>
                  <p className="text-xs text-muted-foreground">
                    Aktivieren, wenn dies keine wiederkehrende Leistung ist.
                  </p>
                </div>
                <Switch checked={isOneTime} onCheckedChange={setIsOneTime} />
              </div>

              <div>
                <Label className="block mb-1">Intervall</Label>
                <Select
                  value={intervalUnit}
                  onValueChange={(v) =>
                    setIntervalUnit(
                      v as (typeof INTERVAL_UNITS)[number]["value"],
                    )
                  }
                  disabled={isOneTime}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVAL_UNITS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        {u.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Erklärung: Wählen Sie den Rhythmus (Tag, Woche, Monat oder
                  Jahr).
                </p>
              </div>
              <div>
                <Label className="block mb-1">
                  Dauer (Anzahl der Intervalle)
                </Label>
                <Input
                  type="number"
                  min={1}
                  value={durationCount}
                  onChange={(e) => setDurationCount(e.target.value)}
                  placeholder="z.B. 3"
                  disabled={isOneTime}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Erklärung: Wie lange die Leistung laufen soll, angegeben als
                  Anzahl der gewählten Einheit. Beispiel: 3 Monate.
                </p>
              </div>

              {!isTimingValid && (
                <p className="text-xs text-destructive">
                  Bitte wählen Sie ein Intervall und geben Sie eine Dauer größer
                  als 0 ein.
                </p>
              )}
            </div>
          )}

          {stepIndex === 5 && (
            <div className="space-y-3">
              <div className="text-sm">
                <p className="font-medium">Bitte prüfen:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>
                    <span className="font-medium">Titel:</span> {name || "–"}
                  </li>
                  <li>
                    <span className="font-medium">Social Media:</span>{" "}
                    {showOnSocialMedia ? "Ja" : "Nein"}
                  </li>
                  <li>
                    <span className="font-medium">Max. gleichzeitig:</span>{" "}
                    {maxConcurrentAllowed.length > 0
                      ? maxConcurrentAllowed
                      : "Keine Beschränkung"}
                  </li>
                  <li>
                    <span className="font-medium">
                      Geforderter Preis (Euro):
                    </span>{" "}
                    {volumeEuro}
                  </li>
                  <li>
                    <span className="font-medium">Art:</span>{" "}
                    {isDonation ? "Spende (Einmalzahlung)" : "Sponsoring"}
                  </li>
                  <li>
                    <span className="font-medium">Zahlungsweise:</span>{" "}
                    {isOneTime ? "Einmalzahlung" : "Wiederkehrend"}
                  </li>
                  {!isOneTime && (
                    <li>
                      <span className="font-medium">Zeitraum:</span>{" "}
                      {Number(durationCount)}{" "}
                      {
                        INTERVAL_UNITS.find((u) => u.value === intervalUnit)
                          ?.label
                      }
                    </li>
                  )}
                </ul>
                {!isFormValid && (
                  <p className="text-xs text-destructive mt-2">
                    Etwas fehlt oder ist ungültig. Gehen Sie zurück und
                    korrigieren Sie es bitte.
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Hinweis: Sie können jederzeit zurückgehen und etwas ändern,
                bevor Sie speichern.
              </p>
            </div>
          )}
        </div>
      </form>
    </CreateBaseDialog>
  );
};

export default CreateAdvertisementDialog;
